import { useEffect, useRef, useState } from "react";
import { ADSENSE_CLIENT_ID, ADSENSE_ENABLED } from "../config/env";
import { ensureAdsenseScript } from "../utils/adsenseLoader";
import "./ad-banner.css";

/**
 * Responsive Ad Banner Component
 * Displays 728x90 ads on desktop and 300x250 or 320x100 on mobile
 * Supports Google Adsense, custom images, or custom content
 * 
 * Props:
 * - slot: Google Adsense slot ID
 * - className: Custom CSS class
 * - type: 'adsense' (default) | 'image' | 'custom'
 * - imageSrc: Image URL (for type='image')
 * - imageAlt: Alt text for image
 * - imageSrcSet: Srcset for responsive images
 * - imageSizes: Sizes attribute for responsive images
 * - imageWidth: Intrinsic width for layout stability
 * - imageHeight: Intrinsic height for layout stability
 * - href: Link URL (for type='image')
 * - target: Link target attribute ('_blank' | '_self' | '_parent' | '_top') - default: '_blank'
 * - children: Custom content (for type='custom')
 */
export default function AdBanner({ 
  enabled = true,
  slot, 
  className = "", 
  type = "adsense",
  imageSrc = null,
  imageAlt = "Banner Iklan",
  imageSrcSet = null,
  imageSizes = null,
  imageWidth = null,
  imageHeight = null,
  imageLoading = "eager",
  fetchPriority = "auto",
  href = null,
  target = "_blank",
  children = null
}) {
  const adsenseActive = ADSENSE_ENABLED && Boolean(ADSENSE_CLIENT_ID);
  const adRef = useRef(null);
  const [imageError, setImageError] = useState(false);
  const pushedRef = useRef(false);

  useEffect(() => {
    if (type !== "adsense" || !slot || !adsenseActive || typeof window === "undefined") {
      return;
    }
    if (!enabled) {
      return;
    }

    const adElement = adRef.current;
    if (!adElement) {
      return;
    }

    if (pushedRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const adStatus = adElement.getAttribute("data-adsbygoogle-status");
      if (adStatus === "done" || pushedRef.current) {
        return;
      }

      ensureAdsenseScript()
        .then(() => {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            pushedRef.current = true;
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (!message.includes("already have ads")) {
              console.error("Ad push error:", err);
            }
          }
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.error("AdSense script load error:", err);
          }
        });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [enabled, slot, type, adsenseActive]);

  if (!enabled) {
    return null;
  }

  if (type === "adsense" && !adsenseActive) {
    return null;
  }

  // If type is image but no imageSrc, show placeholder
  if (type === "image" && !imageSrc) {
    return (
      <div className={`ad-banner-container ${className}`}>
        <div className="ad-banner-wrapper">
          <div className="ad-placeholder">
            <div className="ad-placeholder-content">
              <span className="ad-label">Iklan</span>
              <p>Banner iklan akan ditampilkan di sini</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`ad-banner-container ${className}`}>
      <div className="ad-banner-wrapper">
        {/* Google Adsense */}
        {type === "adsense" && (
          <>
            {slot ? (
              <ins
                ref={adRef}
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client={ADSENSE_CLIENT_ID}
                data-ad-slot={slot}
                data-ad-format="auto"
                data-full-width-responsive="true"
              />
            ) : (
              <div className="ad-placeholder">
                <div className="ad-placeholder-content">
                  <span className="ad-label">Iklan</span>
                  <p>Isi slot AdSense untuk menayangkan iklan</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Custom Image */}
        {type === "image" && imageSrc && !imageError && (
          href ? (
            <a 
              href={href} 
              target={target}
              rel="noopener noreferrer"
              className="ad-image-link"
            >
              <img 
                src={imageSrc} 
                alt={imageAlt}
                srcSet={imageSrcSet || undefined}
              sizes={imageSizes || undefined}
              width={imageWidth || undefined}
              height={imageHeight || undefined}
              className="ad-image"
              loading={imageLoading}
              fetchPriority={fetchPriority}
              onError={() => setImageError(true)}
            />
          </a>
          ) : (
            <img 
              src={imageSrc} 
              alt={imageAlt}
              srcSet={imageSrcSet || undefined}
              sizes={imageSizes || undefined}
              width={imageWidth || undefined}
              height={imageHeight || undefined}
              className="ad-image"
              loading={imageLoading}
              fetchPriority={fetchPriority}
              onError={() => setImageError(true)}
            />
          )
        )}

        {/* Image Error Fallback */}
        {type === "image" && imageError && (
          <div className="ad-placeholder">
            <div className="ad-placeholder-content">
              <span className="ad-label">Iklan</span>
              <p>Gambar banner tidak dapat dimuat</p>
            </div>
          </div>
        )}

        {/* Custom Content */}
        {type === "custom" && children && (
          <div className="ad-custom-content">
            {children}
          </div>
        )}

      </div>
    </div>
  );
}
